import { AccountManagement } from "@/interfaces/accountManagement";
import accountServices from "@/services/account.services";
import { Country, geographicalService } from "@/services/geographical.service";
import { messagesService } from "@/services/messages.service";
import { PageTitleComponent } from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import each from "lodash/each";
import omit from "lodash/omit";
import { Validator } from "vee-validate";
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { creditCardDefault } from "../../declarations";
import Template from "./CreditCardForm.template.vue";

const currentYear = new Date().getFullYear();
@Component({
  mixins: [Template],
  components: {
    PageTitleComponent
  }
})
export default class CreditCardForm extends Vue {
  @Inject() public $validator!: Validator;
  @Getter("user", { namespace: "authModule" })
  public user!: any;
  @Prop() public card!: AccountManagement.CreditCard;
  public fetchedCard: AccountManagement.CreditCard | null = null;

  public model: AccountManagement.CreditCard = cloneDeep(creditCardDefault);
  public editingCard: boolean = false;
  public editingCVV: boolean = false;
  public countries: Country[] = [];
  public loadingCountries = false;
  public states: object[] = [];
  public loadingStates = false;

  public months: string[] = Array(12)
    .fill(null)
    .map((x, i) => String("0" + (i + 1)).slice(-2));

  public years: string[] = Array(21)
    .fill(null)
    .map((x, i) => {
      return String(currentYear + i);
    });
  protected clearFields: boolean = false;
  protected acceptedCreditCards = {
    visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
    amex: /^3[47][0-9]{13}$/,
    discover: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
    "diners-club": /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    jcb: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/
  };

  public hasError(field: string) {
    return this.$validator.errors.has(field);
  }

  public async submit() {
    if (await this.$validator.validateAll()) {
      const parsedModel = omit(
        this.model,
        this.model.card_number.startsWith("*") || this.model.cvv.startsWith("*")
          ? ["card_number", "cvv"]
          : []
      );
      try {
        this.card || this.fetchedCard
          ? await accountServices.editCreditCard(
              this.user.settings.current_account_id,
              this.model.id,
              parsedModel
            )
          : await accountServices.newCreditCard(
              this.user.settings.current_account_id,
              this.model
            );
        this.close();
      } catch (e) {
        messagesService.renderErrorMessage(e);
      }
    }
  }

  public close() {
    this.$router.back();
  }

  public clearHiddenFields() {
    if ((this.card || this.fetchedCard) && !this.clearFields) {
      this.editingCard = true;
      this.clearFields = true;
      this.model.card_number = "";
      this.model.cvv = "";
    }
  }

  public async getCountries() {
    this.loadingCountries = true;
    this.countries = await geographicalService.getCountries();
    this.loadingCountries = false;
  }

  public async countrySelected() {
    this.loadingStates = true;
    this.states = this.model.country_id
      ? await geographicalService.getStates(this.model.country_id)
      : [];
    this.loadingStates = false;
  }

  public setCardType() {
    each(this.acceptedCreditCards, (cardReg, cardType) => {
      if (cardReg.test(this.model.card_number)) {
        this.model.card_type = cardType;
        return false;
      } else {
        this.model.card_type = "";
      }
    });
    if (this.model.cvv) {
      this.$validator.validate("cvv");
    }
  }

  get cardIcon() {
    return this.model.card_type
      ? `fab fa-cc-${this.model.card_type}`
      : "fal fa-credit-card";
  }

  protected async mounted() {
    if (this.$route.name === "edit-credit-card") {
      if (this.card) {
        this.model = { ...this.model, ...cloneDeep(this.card) };
      } else {
        this.model = { ...this.model, ...(await this.loadCard()) };
      }
      this.countrySelected();
    } else {
      this.editingCard = true;
    }

    this.$validator.extend("custom_cc", (value: string) => {
      return new Promise(resolve =>
        resolve(value.startsWith("*") || value.length === 4)
      );
    });

    this.$validator.extend("cvv", {
      validate: (value: string) =>
        this.model.card_type && this.model.card_type === "amex"
          ? value.length === 4
          : value.length === 3
    });

    this.getCountries();
  }

  protected async loadCard(): Promise<AccountManagement.CreditCard> {
    this.fetchedCard = await accountServices.getCreditCard(
      this.user.settings.current_account_id,
      this.$route.params.id
    );
    return this.fetchedCard;
  }
}
